/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { QnaMakerRowMetricStore, RowEditOperationType } from 'src/qnaMaker/core/stores/QnaMakerRowMetricStore';

export const useQnaMetricActions = () => {
    const { rootStore } = useGlobalState();
    const { qnaMetricStore } = rootStore.currentQnaMakerProjectStore;

    const increaseTotalNumberOfClicksBeforeSave = () => {
        qnaMetricStore.increaseTotalNumberOfClicksBeforeSave();
    };

    const increaseQuestionClicksForPair = (pairId: number) => {
        qnaMetricStore.increaseTotalNumberOfClicksBeforeSave();
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).increaseQuestionClicks();
    };

    const setTimestampToStartEditingOrAdding = (timestamp: number) => {
        qnaMetricStore.setTimestampToStartEditingOrAdding(timestamp);
    };

    const resetMetricConfigsAfterSave = () => {
        qnaMetricStore.resetMetricConfigsAfterSave();
    };

    const addNewRowInAddMode = (newPairId: number) => {
        qnaMetricStore.rowMetricStoreHouse.add(new QnaMakerRowMetricStore(newPairId));
    };

    const resetRowMetrics = (pairId: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).resetClicks();
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).resetTimes();
    };

    const setPairOperationType = (pairId: number, operationType: RowEditOperationType) => {
        qnaMetricStore?.rowMetricStoreHouse.findById(pairId)?.setOperationType(operationType);
    };

    const increaseNumberOfClicks = () => {
        qnaMetricStore.increaseTotalNumberOfClicksBeforeSave();
    };

    const increaseAnswerClicksForPair = (pairId: number) => {
        qnaMetricStore.increaseTotalNumberOfClicksBeforeSave();
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).increaseAnswersClicks();
    };

    const increaseMetadataClicksForPair = (pairId: number) => {
        qnaMetricStore.increaseTotalNumberOfClicksBeforeSave();
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).increaseMetadataClicks();
    };

    const increaseAddedQuestionsForPair = (pairId: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).increaseAddedQuestions();
    };

    const increaseEditedQuestionsForPair = (pairId: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).increaseEditedQuestions();
    };

    const increaseDeletedQuestionsForPair = (pairId: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).increaseDeletedQuestions();
    };

    const increaseAddedMetadataForPair = (pairId: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).increaseAddedMetadata();
    };

    const increaseDeletedMetadataForPair = (pairId: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).increaseDeletedMetadata();
    };

    const setTimeTakenToEditQuestions = (pairId: number, time: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).setTimeTakenToEditQuestions(time);
    };

    const setTimeTakenToEditAnswers = (pairId: number, time: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).setTimeTakenToEditAnswers(time);
    };

    const setTimeTakenToEditMetadata = (pairId: number, time: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).setTimeTakenToEditMetadata(time);
    };

    const setTimestampToStartEditing = (pairId: number, time: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).setTimestampToStartEditing(time);
    };

    const getTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode = (pairId: number) =>
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).timeTakenToAddOrDismissAlternateQuestionOutsideEditMode;

    const setTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode = (pairId: number, time: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).setTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode(time);
    };

    const getTimeTakenToAddOrDismissMetadataOutsideEditMode = (pairId: number) =>
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).timeTakenToAddOrDismissMetadataOutsideEditMode;

    const setTimeTakenToAddOrDismissMetadataOutsideEditMode = (pairId: number, time: number) => {
        qnaMetricStore.rowMetricStoreHouse.findById(pairId).setTimeTakenToAddOrDismissMetadataOutsideEditMode(time);
    };

    const getRowInfoOnSubmit = (pairId: number) => qnaMetricStore.rowMetricStoreHouse.findById(pairId).getRowInfoOnSubmit();

    return {
        resetRowMetrics,
        getRowInfoOnSubmit,
        addNewRowInAddMode,
        setPairOperationType,
        increaseNumberOfClicks,
        setTimeTakenToEditAnswers,
        setTimestampToStartEditing,
        setTimeTakenToEditMetadata,
        setTimeTakenToEditQuestions,
        increaseAnswerClicksForPair,
        resetMetricConfigsAfterSave,
        increaseAddedMetadataForPair,
        increaseMetadataClicksForPair,
        increaseQuestionClicksForPair,
        increaseAddedQuestionsForPair,
        increaseEditedQuestionsForPair,
        increaseDeletedMetadataForPair,
        increaseDeletedQuestionsForPair,
        setTimestampToStartEditingOrAdding,
        increaseTotalNumberOfClicksBeforeSave,
        setTimeTakenToAddOrDismissMetadataOutsideEditMode,
        getTimeTakenToAddOrDismissMetadataOutsideEditMode,
        getTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode,
        setTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode
    };
};
